import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// @mui
import {
    Card,
    Table,
    Stack,
    Paper, Button, TableRow, TableBody,
    TableCell,
    Container,
    Typography, TableContainer,
    TablePagination,
    Dialog,
    DialogTitle,
    Alert,
    DialogContent,
    TextField,
    LinearProgress,
    IconButton,
    Popover,
    MenuItem,
    DialogActions,
    InputLabel
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
// components
import i18n from '../i18n';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import { createSubCategory, deleteSubCategory, updateSubCategory, useFetchSubCategories } from '../services/categories_service';
import { useFetchItem } from '../services/item_fetch_service';
import { formatImage } from '../utils/formatImage';
import { uploadFile } from '../utils/fileUploader';
import FileUpload from '../components/file-upload';
// mock

// ----------------------------------------------------------------------

const TABLE_HEAD = [
    { id: 'name', label: i18n.t('Name'), alignRight: false },
    { id: 'thumbnail', label: i18n.t('thumbnail'), alignRight: false },
    { id: 'page_image', label: i18n.t('page_image'), alignRight: false },
    {}
];

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    if (query) {
        return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
    }
    return stabilizedThis.map((el) => el[0]);
}

export default function CategoryPage() {
    const { id: categoryId } = useParams();

    const { data: category, isLoading: isLoadingCategory } = useFetchItem('categories', categoryId);

    const { subCategories, reload: reloadSubCategories, isLoading: isLoadingSubCategories } = useFetchSubCategories(categoryId);

    const [page, setPage] = useState(0);

    const [order, setOrder] = useState('desc');

    const [orderBy, setOrderBy] = useState('name');

    const [filterName, setFilterName] = useState('');

    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [isCreationDialogOpen, setCreationDialogOpen] = useState(false);

    const [editingSubCategory, setEditingSubCategory] = useState(null);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedSubCategoryIdForDelete, setSelectedSubCategoryIdForDelete] = useState(null);


    const { t } = useTranslation();

    const handleOpenMenu = (event, subCategory) => {
        setSelectedSubCategoryIdForDelete(subCategory.id);
        setEditingSubCategory({ anchor: event.currentTarget, subCategory });
    };

    const handleCloseMenu = () => {
        setEditingSubCategory(null);
    };

    const handleOpenDeleteDialog = (id) => {
        setSelectedSubCategoryIdForDelete(id);
        setIsDeleteDialogOpen(true);
    };

    const handleRequestSort = (event, property) => {
        const isDesc = orderBy === property && order === 'desc';
        setOrder(isDesc ? 'asc' : 'desc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPage(0);
        setRowsPerPage(parseInt(event.target.value, 10));
    };

    const handleFilterByName = (event) => {
        setPage(0);
        setFilterName(event.target.value);
    };

    const handleToggleCreateDialog = () => {
        setCreationDialogOpen(prev => !prev);
    };

    const handleEditDialogSubmit = () => {
        reloadSubCategories();
        handleToggleCreateDialog();
        handleCloseMenu();
    }

    const handleConfirmDelete = async () => {
        if (selectedSubCategoryIdForDelete) {
            try {
                // Call the deleteCoach function passing the selectedCoachIdForDelete
                await deleteSubCategory(categoryId, selectedSubCategoryIdForDelete);
                reloadSubCategories();
            } catch (error) {
                console.error('Error deleting coach:', error);
            }

            handleCloseDeleteDialog();
            handleCloseMenu();
        }
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
    };

    const findSubCategoryByName = (name) => {
        if (subCategories) {
            return subCategories.find(val => val.name === name) || -1;
        }
        return -1;
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - subCategories.length) : 0;

    const filtered = applySortFilter(subCategories, getComparator(order, orderBy), filterName);

    const isNotFound = !filtered.length && !!filterName;

    return (
        (isLoadingCategory || isLoadingSubCategories) ? <LinearProgress />
            : <>
                <Helmet>
                    <title> {t('Category')} | MMCX </title>
                </Helmet>

                <Container>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            {`${t('Category')} | ${category?.name}`}
                        </Typography>
                        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleToggleCreateDialog}>
                            {t('New SubCategory')}
                        </Button>
                    </Stack>

                    <Card>
                        <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} />

                        <Scrollbar>
                            <TableContainer>
                                <Table>
                                    <UserListHead
                                        order={order}
                                        orderBy={orderBy}
                                        headLabel={TABLE_HEAD}
                                        rowCount={subCategories.length}
                                        onRequestSort={handleRequestSort}
                                    />
                                    <TableBody>
                                        {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                            const { id, name, page_image: pageImage, thumbnail } = row;

                                            return (
                                                <TableRow hover key={id} tabIndex={-1} role="checkbox">
                                                    <TableCell align="left" >
                                                        <Typography variant="subtitle2" noWrap>
                                                            {name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="left">
                                                        {
                                                            pageImage
                                                                ? <img src={formatImage(pageImage, { h: 400 })} alt={name} style={{ height: '2.5rem' }} />
                                                                : <p>{t('Empty')}</p>
                                                        }
                                                    </TableCell>
                                                    <TableCell align="left">
                                                        {
                                                            thumbnail
                                                                ? <img src={formatImage(thumbnail, { h: 400 })} alt={name} style={{ height: '2.5rem' }} />
                                                                : <p>{t('Empty')}</p>
                                                        }
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="large" color="inherit" onClick={(event) => handleOpenMenu(event, { id, name })}>
                                                            <Iconify icon={'eva:more-vertical-fill'} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {emptyRows > 0 && (
                                            <TableRow style={{ height: 53 * emptyRows }}>
                                                <TableCell colSpan={6} />
                                            </TableRow>
                                        )}
                                    </TableBody>

                                    {isNotFound && (
                                        <TableBody>
                                            <TableRow>
                                                <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                                                    <Paper
                                                        sx={{
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <Typography variant="h6" paragraph>
                                                            {t('Not found')}
                                                        </Typography>

                                                        <Typography variant="body2">
                                                            {t('No results found for')} &nbsp;
                                                            <strong>&quot;{filterName}&quot;</strong>.
                                                            <br /> {t('Try checking for typos or using complete words')}
                                                        </Typography>
                                                    </Paper>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    )}
                                </Table>
                            </TableContainer>
                        </Scrollbar>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            rowsPerPage={rowsPerPage}
                            page={page}
                            count={subCategories.length}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Card>
                </Container>

                <Popover
                    open={Boolean(editingSubCategory)}
                    anchorEl={editingSubCategory?.anchor}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                        sx: {
                            p: 1,
                            width: 175,
                            '& .MuiMenuItem-root': {
                                px: 1,
                                typography: 'body2',
                                borderRadius: 0.75,
                            },
                        },
                    }}
                >
                    <Button onClick={handleToggleCreateDialog}>
                        <MenuItem sx={{ width: 150, display: 'flex', alignItems: 'center' }}>
                            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
                            {t('Edit')}
                        </MenuItem>
                    </Button>
                    <Button onClick={() => handleOpenDeleteDialog(editingSubCategory.subCategory.id)}>
                        <MenuItem sx={{ width: 140, display: 'flex', alignItems: 'center', color: 'error.main' }}>
                            <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
                            {t('Delete')}
                        </MenuItem>
                    </Button>
                </Popover>

                {/* Delete Confirmation Dialog */}
                <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
                    <DialogTitle>{t('Confirm Delete')}</DialogTitle>
                    <DialogContent>{t('Are you sure you want to delete this category?')}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDeleteDialog}>{t('Cancel')}</Button>
                        <Button onClick={handleConfirmDelete} color="secondary">
                            {t('Delete')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <SubCategoryEditDialog
                    category={category}
                    subCategory={editingSubCategory?.subCategory}
                    onSubmit={handleEditDialogSubmit}
                    onClose={handleToggleCreateDialog}
                    open={isCreationDialogOpen}
                    nameChecker={findSubCategoryByName}
                />
            </>
    );
}

const defaultSubCategory = {
    thumbnail: null,
    page_image: null,
    name: ""
};
function SubCategoryEditDialog({ onSubmit, onClose, open, nameChecker, category, subCategory }) {
    const isUpdate = Boolean(subCategory);

    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(isUpdate ? subCategory : defaultSubCategory);

    const nameRef = useRef();

    const checkDataValidity = ({ name, page_image: pageImage, thumbnail }) => {
        if (!isUpdate && nameChecker(name) !== -1 && category.name !== name) {
            return t('This category name already exists');
        }

        if (!pageImage) {
            return t('The page image is required');
        }

        if (!thumbnail) {
            return t('The thumbnail is required');
        }

        return null;
    }


    const handleClose = () => {
        onClose();
        setPayload(defaultSubCategory);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setError(null);

        const error = checkDataValidity(payload);
        if (error) {
            setError(error);
            return;
        }

        const submitData = async () => {
            const thumbnailUrl = await uploadFile(payload.thumbnail);
            payload.thumbnail = thumbnailUrl;
            if (payload.page_image) {
                const pageImageUrl = await uploadFile(payload.page_image);
                payload.page_image = pageImageUrl;
            }
            console.log("payload", payload);

            if (!isUpdate) {
                await createSubCategory(category.id, subCategory.id, payload)
            } else {
                await updateSubCategory(category.id, subCategory.id, payload)
            }
        };

        setIsLoading(true);
        submitData()
            .then(onSubmit)
            .catch(error => setError(error.message))
            .finally(() => setIsLoading(false))
    };

    const { name, page_image: pageImage, thumbnail } = subCategory || defaultSubCategory;
    useEffect(() => {
        setPayload({
            thumbnail,
            page_image: pageImage,
            name
        })
    }, [name, pageImage, thumbnail]);

    return (
        <Dialog onClose={handleClose} open={open}>
            <DialogTitle>{`${t(isUpdate ? "Update SubCategory" : "Create SubCategory")} | ${category?.name}`}</DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={3} mb={3}>
                        {error && <Alert severity="error">{error}</Alert>}
                        <Stack paddingTop={3} spacing={3} direction="row">
                            <Stack gap={1}>
                                <InputLabel id="image-label">{t('image')}</InputLabel>
                                <FileUpload
                                    disabled={isLoading}
                                    edit ratio={1}
                                    defaultFiles={payload.thumbnail}
                                    onChange={(value) => setPayload(prev => ({ ...prev, thumbnail: value }))}
                                />
                            </Stack>
                            <Stack gap={1}>
                                <InputLabel id="wide-image-label">{t('page_image')}</InputLabel>
                                <FileUpload
                                    disabled={isLoading}
                                    defaultFiles={payload.page_image}
                                    onChange={(value) => setPayload(prev => ({ ...prev, page_image: value }))}
                                />
                            </Stack>
                        </Stack>
                        <TextField
                            name="name"
                            labelId="name-label"
                            disabled={isLoading}
                            placeholder={t('Name')}
                            value={payload.name}
                            onChange={(event) => setPayload(prev => ({ ...prev, name: event.target.value }))}
                        />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                        <LoadingButton fullWidth size="large" onClick={handleClose} variant="outlined" disabled={isLoading}>
                            {t('Cancel')}
                        </LoadingButton>
                        <LoadingButton
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isLoading}
                            disabled={isLoading}
                        >
                            {t(isUpdate ? 'Update' : 'create')}
                        </LoadingButton>
                    </Stack>
                </form>
            </DialogContent>
        </Dialog>
    );
}

SubCategoryEditDialog.propTypes = {
    onSubmit: PropTypes.func,
    onClose: PropTypes.func,
    open: PropTypes.bool,
    nameChecker: PropTypes.func,
    category: PropTypes.object,
    subCategory: PropTypes.object
};